import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Checkbox } from '@interfaces/a-organizar/checkbox';
import { OrgChartImportResponse } from '@interfaces/economic-groups/responses/org-chart-import.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { OrgChartImportStateService } from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-import-state.service';
import {
  ReviewChoices,
  ReviewRow,
  buildApplyPayload,
  buildInitialChoices,
  buildReviewRows,
  buildReviewSummary,
  countIncluded,
  describeIncludedCount,
  describeOrgChartImportError,
  formatCnpjForDisplay,
  isNodeIncluded,
  resolveCnpjError,
  resolveNodeName,
  resolveSuggestedPrimary,
} from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-import-review.util';
import { countMembers } from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-tree.util';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { PopoverModule } from 'primeng/popover';

/**
 * Modal "Revisar importação" (card 3578, opção A): a proposta do organograma numa tabela em árvore, para marcar o que
 * entra, digitar ou corrigir CNPJ e aplicar ao grupo. Abre sozinha pelo `reviewRequested$` do
 * `OrgChartImportStateService` (botão Revisar importação do cartão). As regras ficam em `org-chart-import-review.util.ts`.
 */
@Component({
  selector: 'app-org-chart-import-review-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, PopoverModule],
  templateUrl: './org-chart-import-review-modal.component.html',
  styleUrls: ['./org-chart-import-review-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgChartImportReviewModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  readonly state = inject(OrgChartImportStateService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly item = signal<OrgChartImportResponse | null>(null);
  readonly choices = signal<ReviewChoices>({ checked: {}, typedCnpj: {} });
  readonly setAsPrimary = signal(true);
  /** Trecho do documento mostrado no balão das aspas. */
  readonly evidenceRow = signal<ReviewRow | null>(null);

  readonly formatCnpj = formatCnpjForDisplay;

  private readonly proposal = computed(() => this.item()?.proposal ?? null);
  readonly rows = computed(() => {
    const proposal = this.proposal();
    return proposal ? buildReviewRows(proposal) : [];
  });
  readonly summary = computed(() => {
    const proposal = this.proposal();
    return proposal ? buildReviewSummary(proposal) : null;
  });
  readonly warnings = computed(() => this.proposal()?.warnings ?? []);
  readonly includedCount = computed(() => {
    const proposal = this.proposal();
    return proposal ? countIncluded(proposal, this.choices()) : null;
  });
  readonly includedTitle = computed(() => {
    const count = this.includedCount();
    return count ? describeIncludedCount(count) : '';
  });
  readonly suggestedPrimary = computed(() => {
    const proposal = this.proposal();
    return proposal ? resolveSuggestedPrimary(proposal, this.choices()) : null;
  });
  readonly primaryName = computed(() => {
    const node = this.suggestedPrimary();
    return node ? resolveNodeName(node) : '';
  });
  /** Caixa da empresa principal sem rótulo próprio: o texto (com o nome em negrito) fica ao lado, no template. */
  readonly primaryCheckbox = computed<Checkbox | null>(() => {
    const node = this.suggestedPrimary();
    if (!node) return null;
    return {
      id: 'org-chart-review-primary',
      name: 'org-chart-review-primary',
      value: node.tempId,
      label: '',
      isChecked: this.setAsPrimary(),
      isDisabled: false,
    };
  });
  /** Caixa de cada linha; objetos novos a cada mudança para o `app-checkbox` redesenhar. */
  readonly rowCheckboxes = computed(() => {
    const checked = this.choices().checked;
    const map: Record<string, Checkbox> = {};
    for (const row of this.rows()) {
      map[row.node.tempId] = {
        id: `org-chart-review-${row.node.tempId}`,
        name: `org-chart-review-${row.node.tempId}`,
        value: row.node.tempId,
        label: '',
        isChecked: !!checked[row.node.tempId],
        isDisabled: row.isLocked,
      };
    }
    return map;
  });
  readonly canSubmit = computed(
    () => !!this.includedCount()?.total && this.state.canApply() && !this.state.isApplying()
  );

  constructor() {
    super(inject(TranslateService));
    this.state.reviewRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(item => this.open(item));
  }

  open(item: OrgChartImportResponse): void {
    if (!item.proposal) return;
    this.item.set(item);
    this.choices.set(buildInitialChoices(item.proposal));
    this.setAsPrimary.set(!!item.proposal.suggestedPrimaryTempId);
    this.dialogRef?.open(
      '',
      '',
      '',
      'Revisar depois',
      '',
      'org-chart-import-review-modal',
      false,
      false
    );
  }

  close(): void {
    this.dialogRef?.close();
  }

  isFormValid(): boolean {
    return this.canSubmit();
  }

  isIncluded(row: ReviewRow): boolean {
    return isNodeIncluded(row.node, this.choices());
  }

  cnpjError(row: ReviewRow): string | null {
    return resolveCnpjError(row.node, this.choices());
  }

  typedCnpj(row: ReviewRow): string {
    return this.choices().typedCnpj[row.node.tempId] ?? '';
  }

  onRowChecked(row: ReviewRow, checkbox: Checkbox): void {
    if (row.isLocked) return;
    this.choices.update(current => ({
      ...current,
      checked: { ...current.checked, [row.node.tempId]: !!checkbox.isChecked },
    }));
  }

  onCnpjTyped(row: ReviewRow, value: string | null): void {
    this.choices.update(current => ({
      ...current,
      typedCnpj: { ...current.typedCnpj, [row.node.tempId]: value ?? '' },
    }));
  }

  onPrimaryChecked(checkbox: Checkbox): void {
    this.setAsPrimary.set(!!checkbox.isChecked);
  }

  togglePrimary(): void {
    this.setAsPrimary.update(value => !value);
  }

  showEvidence(row: ReviewRow, event: Event, popover: { toggle: (event: Event) => void }): void {
    event.stopPropagation();
    this.evidenceRow.set(row);
    popover.toggle(event);
  }

  discard(): void {
    this.confirmation
      .confirm(
        'Descartar a importação?',
        'Nada do organograma entra no grupo. Para importar de novo, envie o arquivo outra vez.',
        'danger',
        'Voltar à revisão',
        'Descartar'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.state
          .discardCurrent()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(discarded => {
            if (discarded) this.close();
          });
      });
  }

  submitAction(): void {
    const proposal = this.proposal();
    if (!proposal || !this.canSubmit()) return;

    const payload = buildApplyPayload(proposal, this.choices(), this.setAsPrimary());
    const includedTotal = this.includedCount()?.total ?? 0;

    this.state
      .apply(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: tree => {
          const total = tree
            ? (tree.primaryMember ? 1 : 0) + countMembers(tree.rootMembers)
            : includedTotal;
          this.toast.showToastSuccess(
            `Organograma aplicado: ${total} ${total === 1 ? 'empresa' : 'empresas'} no grupo`
          );
          this.close();
        },
        error: error =>
          this.toast.showToastError(
            'Não deu para aplicar o organograma',
            describeOrgChartImportError(error) ??
              'Nada foi gravado no grupo. Confira a conexão e use Aplicar ao grupo de novo.'
          ),
      });
  }

  onDialogClosed(): void {
    this.evidenceRow.set(null);
  }
}
